// Third-party
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Launch as LaunchIcon } from 'components/icons';

// Project imports
import MainCard from 'components/MainCard';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { removeExpiredInvoiceCookies } from 'utils/localStorage';
import CasesTab from './CasesTab.tsx';

import StatusChip from 'components/@extended/StatusChip.tsx';
import PermissionGuard from 'components/security/PermissionGuard.tsx';
import TabsGallery, { TabType } from 'components/TabsGallery.tsx';
import useAuth from 'hooks/useAuth.ts';
import { FormProvider } from 'react-hook-form';
import { useGetInvoicesCountQuery } from 'store/api/invoices.ts';
import {
    useLazyGetDashboardMattersWithFiltersQuery,
    useLazyGetFirmWideMattersWithFiltersQuery,
} from 'store/api/matters.ts';
import { FeatureFlag } from 'types/features.ts';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags.ts';
import FinalizedHours from './finalized-hours/FinalizedHours.tsx';
import HoursCard from './HoursCard.tsx';
import MyAllTimeRecords from './MyAllTimeRecords.tsx';
import MyBDEfforts from './MyBDEfforts.tsx';
import MyDraftTimecards from './MyDraftTimecards';
import MyFinalizedTimecards from './MyFinalizedTimecards.tsx';
import MyPendingInvoicesTab, { MY_PENDING_INVOICES_FILTER } from './MyPendingInvoicesTab.tsx';
import SavedForLaterCard from './SavedForLaterCard';
import { useDashboardForm } from './useDashboardForm.ts';
import { useTiqTime } from './useTiqTime.ts';

const DashBoard = () => {
    const methods = useDashboardForm();
    const { user } = useAuth();
    const userId = user?.id;
    const name = user?.name;
    const myPendingInvoicesCount =
        useGetInvoicesCountQuery({ filters: MY_PENDING_INVOICES_FILTER }, { skip: !userId })?.data?.count || 0;

    const { isEnabled: isTiqTimeEnabled, frontendUrl: tiqTimeFrontendUrl, unPostedMinutesFormatted } = useTiqTime();

    const canSeeOwnTimecards = useHasPermissions(['READ.TIMECARDS.MY_OWN']);
    const canSeeOwnEfforts = useHasPermissions(['READ.EFFORTS.MY_OWN']);
    const canSeeAllTimeTab = canSeeOwnTimecards && canSeeOwnEfforts;
    const isProgressTrackingEnabled = useIsFeatureEnabled(FeatureFlag.DASHBOARD_PROGRESS_TRACKING);
    // The tab reads the user's own employee record, which takes READ.EMPLOYEES.
    const canReadEmployees = useHasPermissions(['READ.EMPLOYEES']);
    const hasProgressTracking = isProgressTrackingEnabled && canReadEmployees;
    const hasDrafts = useIsFeatureEnabled(FeatureFlag.SAVE_AS_DRAFT);

    // Defer mounting the heavy dashboard body (the finalized-hours donut + tabbed tables) one
    // task past mount so the page shell and the header breadcrumb's exit animation paint
    // first; otherwise this synchronous render blocks the main thread and starves the
    // compositor-driven fade — the same contention the list table's useDeferredValue
    // addresses. The timer fires regardless of rAF throttling, so it never
    // strands the content.
    const [showBody, setShowBody] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setShowBody(true), 0);
        return () => clearTimeout(id);
    }, []);

    const TABS_BLOCK_1: TabType[] = [
        {
            id: 'mycases',
            label: t('dashboard.my-cases'),
            component: <CasesTab query={useLazyGetDashboardMattersWithFiltersQuery} />,
            tooltip: t('dashboard.tooltips.my-cases'),
        },
        {
            id: 'firmwidecases',
            label: t('dashboard.firm-wide-cases'),
            component: (
                <CasesTab
                    query={useLazyGetFirmWideMattersWithFiltersQuery}
                    sort="caseNumber,asc"
                    emptyTitleId="dashboard.empty.firm-cases-title"
                    emptyBodyId="dashboard.empty.firm-cases-body"
                    emptySurface="dashboard-firm-cases"
                />
            ),
            tooltip: t('dashboard.tooltips.firm-wide-cases'),
        },
        {
            id: 'mypendinginvoices',
            label: t('dashboard.my-pending-invoices'),
            component: <MyPendingInvoicesTab />,
            icon: (
                <StatusChip
                    size="small"
                    label={myPendingInvoicesCount}
                    type={myPendingInvoicesCount > 0 ? 'error' : 'neutral'}
                />
            ),
        },
    ];

    const TABS_BLOCK_2: TabType[] = [
        {
            id: 'my-draft-timecards',
            label: t('dashboard.my-draft-timecards'),
            component: <MyDraftTimecards />,
        },
        {
            id: 'my-finalized-timecards',
            label: t('dashboard.my-finalized-timecards'),
            component: <MyFinalizedTimecards />,
        },
        {
            id: 'my-bd-efforts',
            label: t('dashboard.my-bd-efforts'),
            component: <MyBDEfforts />,
        },
        ...(canSeeAllTimeTab
            ? [
                  {
                      id: 'my-all-time',
                      label: t('dashboard.my-all-time'),
                      tooltip: t('dashboard.tooltips.my-all-time'),
                      component: <MyAllTimeRecords />,
                  } as TabType,
              ]
            : []),
    ];

    if (userId) {
        removeExpiredInvoiceCookies(userId);
    }

    return (
        <FormProvider {...methods}>
            <PaddedLayout>
                {/* PaddedLayout pads the bottom by no more than the page gutter and is shared page-wide, so
                    the room for the global floating buttons to clear the last row's quick actions is
                    reserved here. */}
                <div className="flex flex-col gap-4 pb-24">
                    <h2 className="text-h2 text-primary flex items-center font-medium text-balance">
                        {getTitle(name ?? null)}
                    </h2>
                    {/* Outer grid = 1 col below md / 2 cols at md+. h-max keeps each column at
                        content height. Each grid column is already 50% at md+, so the right card
                        fills its column — an inner md:w-1/2 would double-halve it to 25% and clip
                        the table. */}
                    {showBody && (
                        <div className="mt-2 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                            <div className="flex h-max flex-col gap-[30px]">
                                <PermissionGuard permissions={['READ.DASHBOARD.FINALIZED_HOURS']}>
                                    <div>{hasProgressTracking ? <HoursCard /> : <FinalizedHours />}</div>
                                </PermissionGuard>
                                <div>
                                    {isTiqTimeEnabled && tiqTimeFrontendUrl && (
                                        <a
                                            href={tiqTimeFrontendUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:text-link mb-2 flex w-fit items-center gap-1 font-normal no-underline hover:underline"
                                        >
                                            <LaunchIcon fontSize="small" />
                                            {unPostedMinutesFormatted}
                                            <FormattedMessage id="tiqtime.suggested-by-tiqtime" />
                                        </a>
                                    )}
                                    <MainCard contentClassName="px-0 pt-0">
                                        <TabsGallery
                                            tabs={TABS_BLOCK_2}
                                            className="mb-0"
                                            tabGroupId="dashboard-tabs-block-2"
                                        />
                                    </MainCard>
                                </div>
                            </div>
                            <div className="flex h-max flex-col gap-[30px]">
                                {hasDrafts ? <SavedForLaterCard /> : null}
                                <MainCard contentClassName="px-0 pt-0">
                                    <TabsGallery
                                        tabs={TABS_BLOCK_1}
                                        className="mb-0"
                                        tabGroupId="dashboard-tabs-block-1"
                                    />
                                </MainCard>
                            </div>
                        </div>
                    )}
                </div>
            </PaddedLayout>
        </FormProvider>
    );
};

export const getTitle = (name: string | null) => {
    if (!name) {
        return '';
    }

    const firstName = name.split(' ')[0] || '';
    const today = new Date();
    const curHr = today.getHours();

    if (curHr < 12) {
        return t('dashboard.good-morning', { name: firstName });
    } else if (curHr < 18) {
        return t('dashboard.good-afternoon', { name: firstName });
    } else {
        return t('dashboard.good-evening', { name: firstName });
    }
};

export default DashBoard;
